import { useMemo } from "react";
import { cn } from "../../utils";

/**
 * A compact, read-only diff for Chat's file-editing tool calls (P4.2 / D4). Edit
 * → old_string/new_string; MultiEdit → edits[]; Write → the new content as
 * additions. Line-based (added = emerald, removed = rose), capped so a large
 * change can't swallow the rail. Not a full diff engine — a glanceable preview of
 * "what changed". Renders nothing for non-editing tools.
 */

interface DiffLine {
  kind: "add" | "del";
  text: string;
}

const MAX_LINES = 24;

function lines(s: unknown): string[] {
  return typeof s === "string" && s.length ? s.split("\n") : [];
}

function diffForEdit(oldStr: unknown, newStr: unknown): DiffLine[] {
  const out: DiffLine[] = [];
  for (const t of lines(oldStr)) out.push({ kind: "del", text: t });
  for (const t of lines(newStr)) out.push({ kind: "add", text: t });
  return out;
}

function buildDiff(name: string, input: unknown): DiffLine[] | null {
  if (!input || typeof input !== "object") return null;
  const o = input as Record<string, unknown>;
  if (name === "Edit") return diffForEdit(o.old_string, o.new_string);
  if (name === "Write") return lines(o.content).map((t) => ({ kind: "add" as const, text: t }));
  if (name === "MultiEdit" && Array.isArray(o.edits)) {
    const out: DiffLine[] = [];
    for (const e of o.edits as Array<Record<string, unknown>>) {
      out.push(...diffForEdit(e?.old_string, e?.new_string));
    }
    return out;
  }
  return null;
}

export function isDiffTool(name: string): boolean {
  return name === "Edit" || name === "Write" || name === "MultiEdit";
}

export function ToolDiff({ name, input }: { name: string; input: unknown }) {
  const diff = useMemo(() => buildDiff(name, input), [name, input]);
  if (!diff || diff.length === 0) return null;
  const shown = diff.slice(0, MAX_LINES);
  const extra = diff.length - shown.length;
  return (
    <pre className="mt-1 overflow-x-auto rounded-md border border-border/40 bg-secondary/30 p-2 font-mono type-subtext leading-body">
      {shown.map((l, i) => (
        <div
          key={i}
          className={cn(
            "whitespace-pre",
            l.kind === "add"
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-rose-600 dark:text-rose-400",
          )}
        >
          {l.kind === "add" ? "+ " : "- "}
          {l.text || " "}
        </div>
      ))}
      {extra > 0 ? (
        <div className="text-muted-foreground">… {extra} more line{extra === 1 ? "" : "s"}</div>
      ) : null}
    </pre>
  );
}
